import { useCallback } from 'react';

import type { CoffeeQuery, CoffeeQueryVariables } from '@/api/generated/graphql';
import { COFFEE_QUERY } from '@/api/queries/coffee';
import { useAuthedClient } from '@/api/useAuthedClient';
import type { Coffee } from '@/types/feed';

import { useGraphQLQuery } from './useGraphQLQuery';

interface UseCoffeeReturn {
  coffee: Coffee | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useCoffee(id: string | undefined): UseCoffeeReturn {
  const authedClient = useAuthedClient();

  const { data, isLoading, error, refetch } = useGraphQLQuery<CoffeeQuery>({
    fetcher: (signal) =>
      authedClient.request<CoffeeQuery, CoffeeQueryVariables>({
        document: COFFEE_QUERY,
        variables: { id: id ?? '' },
        signal,
      }),
    deps: [id],
    enabled: Boolean(id),
  });

  const wrappedRefetch = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    coffee: data?.coffee ?? null,
    isLoading,
    error,
    refetch: wrappedRefetch,
  };
}
